import { useCallback, useState } from "react";
import { v4 } from "uuid";
import { Plus } from "lucide-react";
import t from "@/lang/lang";
import { toast } from "sonner";
import {
  newAddress,
  type Address,
} from "@/services/backend/addresses/types/address";
import {
  CreateBuildingRequest,
  useBuildingsCreateMutation,
} from "@/services/backend/buildings/api/building-api";
import { useActiveCompany } from "@/shared/lib/active-company/active-company-provider";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { TextInput } from "@/shared/components/form/text-input";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import { CustomerAddressCard } from "@/services/backend/addresses/components/customer-address-card";
import { BuildingAddressSelect } from "@/services/backend/buildings/components/building-address-select";

export function AddBuildingComponent({
  customerId,
  initialAddress,
  triggerLabel = t("Gebäude"),
  onCreated,
}: {
  customerId: string;
  initialAddress?: Address;
  triggerLabel?: string;
  onCreated?: (buildingId: string) => void;
}) {
  const { companyId } = useActiveCompany();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState<CreateBuildingRequest>(
    newRequest(customerId, companyId, initialAddress),
  );
  const [post, { isLoading, error, reset }] = useBuildingsCreateMutation();

  const createBuilding = () => {
    if (isLoading) return;
    const id = request.id;
    post(request)
      .unwrap()
      .then(() => {
        setOpen(false);
        setRequest(newRequest(customerId, companyId, initialAddress));
        reset();
        onCreated?.(id);
        toast(t("Gebäude erstellt"));
      });
  };

  const handleOpenChange = useCallback(
    (value: boolean) => {
      if (!value) {
        setRequest(newRequest(customerId, companyId, initialAddress));
      }
      setOpen(value);
      reset();
    },
    [companyId, customerId, initialAddress, reset],
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <CommonDialogTrigger icon={Plus} label={triggerLabel} size="sm" />
      <DialogContent className="sm:max-w-6xl">
        <CommonDialogHeader
          title={t("Gebäude erstellen")}
          description={t(
            "Hier kann ein Gebäude mit optionaler Adresse erstellt werden.",
          )}
        />
        <div className="grid gap-4">
          <TextInput
            label={t("Name")}
            field="name"
            text={request.name}
            onTextChange={(name) => setRequest({ ...request, name })}
            errors={null}
          />
          <BuildingAddressSelect
            customerId={customerId}
            currentAddress={request.address}
            onAddressSelect={(address) => setRequest({ ...request, address })}
          />
          <CustomerAddressCard
            customerAddress={request.address}
            onCustomerAddressChange={(address) =>
              setRequest({ ...request, address })
            }
            title={t("Adresse")}
            description={t("Die Adresse ist optional.")}
          />
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          actionLabel={t("Erstellen")}
          onClickAction={createBuilding}
        />
      </DialogContent>
    </Dialog>
  );
}

function newRequest(
  customerId: string,
  companyId: string,
  initialAddress?: Address,
): CreateBuildingRequest {
  return {
    id: v4(),
    companyId,
    customerId,
    name: "",
    address: initialAddress ?? newAddress(),
  };
}
